/**
 * State transitions for `TourProvider`. Pure: recording, storage and network live in the provider.
 */
import type { Tour, TourOutcome, TourProgress } from "../../types/tours";
import {
  chainedDataRun,
  continuationOf,
  dueRun,
  findProgress,
  withRecordedRun,
  type RenderContext,
  type TourRun,
} from "./tourEngine";
import type { ActiveTour, PageRegistration, TourConfigStatus } from "./tourContext";

export interface TourState {
  /** `${userId}|${orgId}` the config below belongs to. */
  scope: string | null;
  status: TourConfigStatus;
  tours: Tour[];
  progress: TourProgress[];
  active: ActiveTour | null;
  queue: TourRun[];
  pages: Record<string, PageRegistration>;
  /**
   * How the next tour of a `via: "flow"` continuation should be shown, set when the tour before it
   * finished. Taken by the next run to start if it is that tour, and dropped by any other start or
   * by leaving the page (`close`), so it never numbers a run that was not the walkthrough's next
   * part.
   */
  carryOn: CarryOn | null;
}

export interface CarryOn {
  tourKey: string;
  stepOffset?: number;
  returnTo?: string;
}

export type TourAction =
  | { type: "reset" }
  | { type: "loadStart"; scope: string }
  | { type: "loadSucceeded"; scope: string; tours: Tour[]; progress: TourProgress[] }
  | { type: "loadFailed"; scope: string }
  /**
   * `replace` puts the run on screen even over an active one, and clears the queue: only a preview
   * uses it, because a URL that names a draft must show that draft rather than lose to whatever
   * published tour got there first. The replaced run is closed, not ended, so nothing is recorded.
   */
  | { type: "start"; run: TourRun; replace?: boolean }
  | { type: "queue"; run: TourRun }
  | { type: "step"; delta: 1 | -1 }
  /**
   * Back, to an exact step (out of range is ignored). `rearm` makes that step's press, and every
   * press after it, stop counting as spent, so the step is shown as the coach first met it.
   */
  | { type: "goto"; stepIndex: number; rearm?: boolean }
  /**
   * The current step's control has been pressed: by the coach, on a `ClickToContinue` step, or by
   * the tour itself, on an `OpenAnchor` one. Recorded against the step's key, so revisiting it
   * renders read-and-Next instead of asking for — or repeating — a press that would close what the
   * press opened. It does not move the tour; the caller advances separately when it should.
   */
  | { type: "anchorUsed" }
  | { type: "end"; outcome: TourOutcome; context: RenderContext }
  | { type: "close" }
  | { type: "doneElsewhere"; tourKey: string; seenStepKeys: string[] }
  | { type: "updatePage"; featureKey: string; registration: PageRegistration }
  | { type: "unregisterPage"; featureKey: string };

export const initialTourState: TourState = {
  scope: null,
  status: "idle",
  tours: [],
  progress: [],
  active: null,
  queue: [],
  pages: {},
  carryOn: null,
};

/** A queued run, re-checked at the moment it would start. */
const revalidate = (
  state: TourState,
  candidate: TourRun,
  progress: TourProgress[],
  context: RenderContext
): TourRun | null => {
  if (candidate.tour.kind === "Data" && !state.pages[candidate.tour.feature]?.hasData) {
    return null;
  }
  if (candidate.replay) return candidate;
  return dueRun(candidate.tour, findProgress(progress, candidate.tour.key), context);
};

/**
 * Whether ending the active run puts another run on screen (a queued run, or the chained data
 * tour). The provider asks before it ends a run: a board modal the run opened is closed only when
 * something else is about to take the screen, and left for the coach, with whatever they typed in
 * it, when the tour simply goes away.
 */
export const runFollowsEnd = (
  state: TourState,
  outcome: TourOutcome,
  context: RenderContext
): boolean => endActive(state, outcome, context).active !== null;

const endActive = (state: TourState, outcome: TourOutcome, context: RenderContext): TourState => {
  const ended = state.active?.run;
  if (!ended) return state;

  const progress = ended.replay ? state.progress : withRecordedRun(state.progress, ended, outcome);
  const queue = state.queue.filter((run) => run.tour.key !== ended.tour.key);

  let nextRun: TourRun | null = null;
  while (queue.length > 0 && nextRun === null) {
    nextRun = revalidate(state, queue.shift() as TourRun, progress, context);
  }
  // A preview shows exactly the one tour the admin asked for: chaining the real data tour behind
  // it would put a published tour on screen from a URL that named a draft.
  // Nor does a replay of a Guided tours entry that names no data tour to follow it.
  if (!ended.preview && !ended.standalone) {
    nextRun ??= chainedDataRun(
      state.tours,
      progress,
      ended,
      state.pages[ended.tour.feature]?.hasData ?? false,
      context
    );
  }

  const continuation = ended.preview ? null : continuationOf(ended.tour.key);
  const continues = continuation !== null && nextRun?.tour.key === continuation.next;
  // One walkthrough: skipping its first part skips the rest, and counts the rest as skipped too.
  if (continues && outcome === "Skipped" && nextRun) {
    const skippedRest = nextRun;
    return {
      ...state,
      progress: skippedRest.replay ? progress : withRecordedRun(progress, skippedRest, "Skipped"),
      queue: queue.filter((run) => run.tour.key !== skippedRest.tour.key),
      active: null,
      carryOn: null,
    };
  }
  const shownSoFar = (ended.stepOffset ?? 0) + ended.steps.length;
  if (nextRun) {
    nextRun = {
      ...nextRun,
      // The same page, so a Guided tours replay goes back to the list after whatever follows.
      ...(ended.returnTo ? { returnTo: ended.returnTo } : {}),
      ...(continues && continuation?.numbered ? { stepOffset: shownSoFar } : {}),
    };
  }
  const carryOn: CarryOn | null =
    outcome === "Finished" && continuation?.via === "flow" && !continues
      ? {
          tourKey: continuation.next,
          ...(continuation.numbered ? { stepOffset: shownSoFar } : {}),
          ...(continuation.carriesReturn && ended.returnTo ? { returnTo: ended.returnTo } : {}),
        }
      : null;

  return {
    ...state,
    progress,
    queue,
    active: nextRun ? { run: nextRun, stepIndex: 0, satisfiedStepKeys: [] } : null,
    carryOn,
  };
};

/**
 * The run that would take the screen if the active run ended with `outcome` now, or null. The
 * overlay asks it with "Finished" to know whether the last step leads on (Next) or ends (Finish).
 */
export const runAfterEnd = (
  state: TourState,
  outcome: TourOutcome,
  context: RenderContext
): TourRun | null => endActive(state, outcome, context).active?.run ?? null;

/** `run` as the next part of the walkthrough `carryOn` describes, when it is that tour. */
const withCarryOn = (run: TourRun, carryOn: CarryOn | null): TourRun =>
  carryOn && carryOn.tourKey === run.tour.key
    ? {
        ...run,
        ...(carryOn.stepOffset !== undefined ? { stepOffset: carryOn.stepOffset } : {}),
        ...(carryOn.returnTo && !run.returnTo ? { returnTo: carryOn.returnTo } : {}),
      }
    : run;

export const tourReducer = (state: TourState, action: TourAction): TourState => {
  switch (action.type) {
    case "reset":
      return { ...initialTourState, pages: state.pages };

    case "loadStart":
      // A new user or organization: whatever was running belonged to the old one.
      return { ...initialTourState, pages: state.pages, scope: action.scope, status: "loading" };

    case "loadSucceeded":
      if (action.scope !== state.scope) return state;
      return { ...state, status: "ready", tours: action.tours, progress: action.progress };

    case "loadFailed":
      if (action.scope !== state.scope) return state;
      return { ...state, status: "disabled", tours: [], progress: [] };

    case "start":
      if (state.status !== "ready" || action.run.steps.length === 0) return state;
      if (action.replace) {
        return {
          ...state,
          queue: [],
          carryOn: null,
          active: { run: action.run, stepIndex: 0, satisfiedStepKeys: [] },
        };
      }
      if (state.active) return state;
      return {
        ...state,
        carryOn: null,
        active: { run: withCarryOn(action.run, state.carryOn), stepIndex: 0, satisfiedStepKeys: [] },
      };

    case "queue":
      if (
        state.queue.some((run) => run.tour.key === action.run.tour.key) ||
        state.active?.run.tour.key === action.run.tour.key
      ) {
        return state;
      }
      return { ...state, queue: [...state.queue, action.run] };

    case "step": {
      if (!state.active) return state;
      const stepIndex = state.active.stepIndex + action.delta;
      if (stepIndex < 0 || stepIndex >= state.active.run.steps.length) return state;
      return { ...state, active: { ...state.active, stepIndex } };
    }

    case "goto": {
      if (!state.active) return state;
      const { stepIndex } = action;
      if (stepIndex < 0 || stepIndex >= state.active.run.steps.length) return state;
      if (stepIndex === state.active.stepIndex) return state;
      if (!action.rearm) return { ...state, active: { ...state.active, stepIndex } };
      const undone = new Set(state.active.run.steps.slice(stepIndex).map((step) => step.key));
      const satisfiedStepKeys = state.active.satisfiedStepKeys.filter((key) => !undone.has(key));
      return { ...state, active: { ...state.active, stepIndex, satisfiedStepKeys } };
    }

    case "anchorUsed": {
      const active = state.active;
      const stepKey = active?.run.steps[active.stepIndex]?.key;
      if (!active || !stepKey || active.satisfiedStepKeys.includes(stepKey)) return state;
      return {
        ...state,
        active: { ...active, satisfiedStepKeys: [...active.satisfiedStepKeys, stepKey] },
      };
    }

    case "end":
      return endActive(state, action.outcome, action.context);

    case "close":
      // Leaving the page also drops a carry-on: the walkthrough's next part was for this visit.
      // The new roster's hand-off has already taken the way back by then (`handOff`).
      if (!state.active && state.queue.length === 0 && state.carryOn === null) return state;
      return { ...state, active: null, queue: [], carryOn: null };

    case "doneElsewhere": {
      const existing = findProgress(state.progress, action.tourKey);
      const merged: TourProgress = {
        tourKey: action.tourKey,
        outcome: existing?.outcome ?? null,
        seenStepKeys: Array.from(
          new Set([...(existing?.seenStepKeys ?? []), ...action.seenStepKeys])
        ),
      };
      const progress = existing
        ? state.progress.map((entry) => (entry.tourKey === action.tourKey ? merged : entry))
        : [...state.progress, merged];
      const closesActive =
        state.active?.run.tour.key === action.tourKey && !state.active.run.replay;
      return {
        ...state,
        progress,
        queue: state.queue.filter((run) => run.tour.key !== action.tourKey),
        active: closesActive ? null : state.active,
      };
    }

    case "updatePage": {
      const current = state.pages[action.featureKey];
      if (
        current &&
        current.ready === action.registration.ready &&
        current.hasData === action.registration.hasData
      ) {
        return state;
      }
      return { ...state, pages: { ...state.pages, [action.featureKey]: action.registration } };
    }

    case "unregisterPage": {
      if (!(action.featureKey in state.pages)) return state;
      const { [action.featureKey]: _removed, ...pages } = state.pages;
      return { ...state, pages };
    }
  }
};
